/**
 * ч.1279: дублі карток банківського рахунку (вкладка «Банк» картки організації / контрагента).
 *
 * Той самий рахунок буває заведено кількома картками одного власника: з 1С і вручну, або двічі
 * в самій 1С. Документи тоді розділені між картками, залишок і оборотно-сальдова відомість — на
 * два рядки, а виписка банку йде лише на одну. Дубль = той самий IBAN І та сама валюта
 * (мультивалютний IBAN — окрема картка на кожну валюту, це не дубль); сервер позначає такі
 * картки полем `same_iban_cards`.
 *
 * Обʼєднання переносить на обрану основну картку всі документи, рухи грошей і проводки — і в
 * періоді під забороною змін (суми й рахунки не змінюються). Незворотне, тому доступне лише
 * власнику та адміністратору акаунта.
 */
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { refs, type BankAccount } from '@/api/refs';
import { usePermissions } from '@/auth/usePermissions';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { formatIban } from '@/shared/ukr_classifiers';
import { extractApiError } from '@/utils/extractApiError';
import TchTable from '@/shared/components/TchTable';

interface Props {
  accounts: BankAccount[];
  currencies: { id: string; code: string }[];
  onMerged: () => void;
}

const norm = (iban: string) => iban.replace(/\s+/g, '').toUpperCase();
const day = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('uk-UA') : '');

/** Групи дублів: той самий IBAN і та сама валюта. */
export function duplicateGroups(accounts: BankAccount[]): BankAccount[][] {
  const by = new Map<string, BankAccount[]>();
  for (const a of accounts) {
    if (!a.same_iban_cards) continue;
    const key = `${norm(a.iban)}|${a.currency_id}`;
    by.set(key, [...(by.get(key) ?? []), a]);
  }
  return [...by.values()].filter((g) => g.length > 1);
}

/** Основна за замовчуванням: позначена основною, інакше — з найбільшою кількістю документів. */
export function defaultMain(group: BankAccount[]): string {
  const sorted = [...group].sort((a, b) => (
    Number(b.is_main) - Number(a.is_main)
    || (b.bank_documents ?? 0) - (a.bank_documents ?? 0)
    || (b.last_document_date ?? '').localeCompare(a.last_document_date ?? '')
  ));
  return sorted[0]?.id ?? '';
}

function cardName(a: BankAccount): string {
  return a.account_name?.trim() || 'без назви';
}

function Group({ group, currency, canMerge, onMerged }: {
  group: BankAccount[]; currency: string; canMerge: boolean; onMerged: () => void;
}) {
  const confirm = useConfirm();
  const toast = useToast();
  const [main, setMain] = useState(() => defaultMain(group));
  const dups = group.filter((a) => a.id !== main).map((a) => a.id);
  const mainCard = group.find((a) => a.id === main);
  const merge = useMutation({
    mutationFn: () => refs.mergeBankAccounts(main, dups),
    onSuccess: (r) => {
      const n = Object.values(r.moved).reduce((s, v) => s + v, 0);
      toast.show({ type: 'success', message: `Картки обʼєднано: ${r.merged}; перенесено посилань: ${n}.` });
      onMerged();
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося обʼєднати картки') }),
  });
  const iban = formatIban(group[0]!.iban);

  return (
    <div style={{
      background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', borderRadius: 6,
      padding: '0.6rem 0.75rem', marginBottom: '0.75rem',
    }}>
      <div style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>
        Рахунок {iban} ({currency}) заведено картками: {group.length}
      </div>
      <div style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
        Документи рахунку розділені між картками, залишок рахується окремо по кожній, а виписка з
        банку йде лише на одну. Оберіть основну картку — документи, рухи грошей і проводки інших
        перейдуть на неї.
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 70, textAlign: 'center' }}>основна</th>
            <th>Назва картки</th>
            <th style={{ width: 120, textAlign: 'right' }}>Документів банку</th>
            <th>Період документів</th>
            <th>Звідки</th>
          </tr>
        </thead>
        <tbody>
          {group.map((a) => (
            <tr key={a.id}>
              <td style={{ textAlign: 'center' }}>
                <input type="radio" name={`merge-main-${group[0]!.id}`} style={{ width: 'auto', margin: 0 }}
                  aria-label={`основна: ${cardName(a)}`} checked={main === a.id} disabled={!canMerge}
                  onChange={() => setMain(a.id)} />
              </td>
              <td>
                {cardName(a)}
                {a.is_main && <span style={{ color: 'var(--muted)' }}> · основний рахунок організації</span>}
                {a.has_api_connection && <span style={{ color: 'var(--muted)' }}> · виписка з банку</span>}
              </td>
              <td style={{ textAlign: 'right' }}>{a.bank_documents ?? 0}</td>
              <td>
                {a.first_document_date
                  ? `${day(a.first_document_date)} — ${day(a.last_document_date)}`
                  : <span style={{ color: 'var(--muted)' }}>—</span>}
              </td>
              <td>
                {a.external_source ? (
                  <>
                    <span title="Імпортовано з 1С" style={{ color: '#2563eb', border: '1px solid #bfdbfe',
                      borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', marginRight: 6 }}>I</span>
                    з 1С
                  </>
                ) : 'заведено вручну'}
              </td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
        <button type="button" className="primary" disabled={!canMerge || !main || merge.isPending}
          title={canMerge ? undefined : 'Обʼєднувати картки може лише власник або адміністратор акаунта'}
          onClick={async () => {
            if (await confirm({
              title: 'Обʼєднати картки рахунку?',
              message: `Документи, рухи грошей і проводки інших карток (${dups.length}) перейдуть на картку `
                + `«${mainCard ? cardName(mainCard) : ''}» — і в періоді під забороною змін (суми й рахунки `
                + 'не змінюються). Інші картки зникнуть зі списку. Скасувати це не можна.',
              okLabel: 'обʼєднати',
            })) merge.mutate();
          }}>
          {merge.isPending ? 'обʼєдную…' : 'обʼєднати картки'}
        </button>
        {!canMerge && (
          <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            Обʼєднувати картки може лише власник або адміністратор акаунта.
          </span>
        )}
      </div>
    </div>
  );
}

export function BankAccountDuplicates({ accounts, currencies, onMerged }: Props) {
  const { isOwnerOrAdmin } = usePermissions();
  const groups = useMemo(() => duplicateGroups(accounts), [accounts]);
  if (groups.length === 0) return null;
  const code = (id: string) => currencies.find((c) => c.id === id)?.code ?? '';
  return (
    <>
      {groups.map((g) => (
        <Group key={g.map((a) => a.id).join('|')} group={g} currency={code(g[0]!.currency_id)}
          canMerge={isOwnerOrAdmin} onMerged={onMerged} />
      ))}
    </>
  );
}
